import _extends from '@babel/runtime/helpers/esm/extends';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useState, useEffect } from 'react';
import { aggregatedClasses } from '@elliemae/ds-classnames';
import moment from 'moment';
import { g as getTimeValuesFromTime, A as AM, c as commonInputProps, p as placeholderFormat, P as PLACEHOLDER_TIME, a as ARROW_UP, f as formatMeridiem, b as ARROW_DOWN, d as PM, S as SHIFT, B as BACKSPACE, E as END, s as setNativeValue, e as focusNextInput, H as HOME, h as ESCAPE, i as focusPreviousInput, j as parseTimeNumberFromText, k as getValidTimeNumber, G as GENERAL_TIME_RANGE, l as formatSeconds, m as formatMinutes, n as formatHour, o as shouldFocusNextInput, q as isArrowIncrementDecrement, r as HOUR_RANGE_12, t as HOUR_RANGE_24 } from '../utils-0a7eb302.js';
import '@elliemae/ds-utilities/timeUtils';
import '@elliemae/ds-utilities/utils';

var blockName = 'input-times-group';
var InputTimesGroup = aggregatedClasses('div')(blockName);
var TimeInput = aggregatedClasses('input')(blockName, 'input');
var Divider = aggregatedClasses('span')(blockName, 'divider', function (_ref) {
  var value = _ref.value;
  return {
    value: value
  };
}); // eslint-disable-next-line react/prop-types

var TimeDivider = function TimeDivider(_ref2) {
  var _ref2$content = _ref2.content,
      content = _ref2$content === void 0 ? ':' : _ref2$content,
      _ref2$value = _ref2.value,
      value = _ref2$value === void 0 ? '' : _ref2$value;
  return /*#__PURE__*/React.createElement(Divider, {
    value: !!value
  }, content);
};

var TimeInputs = function TimeInputs(_ref3) {
  var time = _ref3.time,
      format = _ref3.format,
      use12Hours = _ref3.use12Hours,
      _ref3$step = _ref3.step,
      step = _ref3$step === void 0 ? 1 : _ref3$step,
      showHours = _ref3.showHours,
      showMinutes = _ref3.showMinutes,
      showSeconds = _ref3.showSeconds,
      onChange = _ref3.onChange,
      disabled = _ref3.disabled,
      innerRef = _ref3.innerRef,
      clearable = _ref3.clearable,
      ariaLabel = _ref3['arial-label'],
      ariaLabel2 = _ref3['aria-label'];

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      meridiem = _useState2[0],
      setMeridiem = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      hours = _useState4[0],
      setHours = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      minutes = _useState6[0],
      setMinutes = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      hasInputValue = _useState8[0],
      setHasInputValue = _useState8[1];

  var _useState9 = useState(''),
      _useState10 = _slicedToArray(_useState9, 2),
      seconds = _useState10[0],
      setSeconds = _useState10[1];

  var _useState11 = useState(''),
      _useState12 = _slicedToArray(_useState11, 2),
      currentKey = _useState12[0],
      setCurrentKey = _useState12[1];

  var _useState13 = useState(false),
      _useState14 = _slicedToArray(_useState13, 2),
      hasInputBeenTriggered = _useState14[0],
      setHasInputBeenTriggered = _useState14[1];

  var _useState15 = useState(null),
      _useState16 = _slicedToArray(_useState15, 2),
      lastTypedNumber = _useState16[0],
      setLastTypedNumber = _useState16[1];
  useEffect(function () {
    if (time && moment(time).isValid()) {
      var timeValues = getTimeValuesFromTime(time, format, use12Hours);
      setMeridiem(timeValues.meridiem);
      setHours(timeValues.hours);
      setMinutes(timeValues.minutes);
      setSeconds(timeValues.seconds);
    } else {
      setMeridiem('');
      setHours('');
      setMinutes('');
      setSeconds('');
    }
  }, [time, format, use12Hours]);
  useEffect(function () {
    var selectedTime = time || moment();

    if (isTimeCompletelySet() && selectedTime.hours && hasInputBeenTriggered) {
      if (showHours && hours) selectedTime.hours(hours);
      if (showMinutes && minutes) selectedTime.minutes(minutes);
      if (showSeconds && seconds) selectedTime.seconds(seconds);

      if (use12Hours && meridiem) {
        var nextHours = meridiem.toLowerCase() === AM ? hours % 12 : hours % 12 + 12;
        selectedTime.hour(nextHours);
      }

      onChange(selectedTime);
      setHasInputBeenTriggered(false);
    }
  }, [hours, minutes, seconds, meridiem]);

  var focusNextInputIfNeeded = function focusNextInputIfNeeded(currentEl, max, number) {
    if (shouldFocusNextInput(max, number, currentEl.value) && !isArrowIncrementDecrement(currentKey) && !hasInputValue) {
      focusNextInput(currentEl);
    }
  };

  var getNextTimeValue = function getNextTimeValue(value) {
    var incrementing = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var parsedValue = parseTimeNumberFromText(value);
    return incrementing ? parsedValue + step : parsedValue - step;
  };

  var onInputFocus = function onInputFocus(e) {
    e.preventDefault();
    e.stopPropagation();
    e.target.select();
  };

  var onClick = function onClick(e) {
    if (e.target.value) {
      setHasInputValue(true);
    }
  }; // eslint-disable-next-line max-statements


  var onKeyDown = function onKeyDown(event, max) {
    var target = event.target;
    var value = target.value;
    setLastTypedNumber(event.key);

    switch (event.key) {
      case ARROW_UP:
        {
          event.preventDefault();
          setCurrentKey(event.key);
          var incrementedValue = getNextTimeValue(value);
          setNativeValue(event.target, incrementedValue);
          break;
        }

      case ARROW_DOWN:
        {
          event.preventDefault();
          setCurrentKey(event.key);
          var decrementedValue = getNextTimeValue(value, false);
          setNativeValue(event.target, decrementedValue);
          break;
        }

      case BACKSPACE:
        {
          event.preventDefault();

          if (!value) {
            focusPreviousInput(event.target);
          } else {
            setNativeValue(event.target, '');
          }

          break;
        }

      case ESCAPE:
        {
          event.preventDefault();
          event.target.blur();
          break;
        }

      case HOME:
        {
          event.preventDefault();
          setNativeValue(event.target, String(max));
          focusNextInput(event.target);
          break;
        }

      case END:
        {
          event.preventDefault();
          setNativeValue(event.target, '00');
          focusNextInput(event.target);
          break;
        }

      case SHIFT:
        event.preventDefault();
        setCurrentKey(event.key);
        break;
    }
  };

  var isTimeCompletelySet = function isTimeCompletelySet() {
    var neededValues = [];
    if (showHours) neededValues = [].concat(_toConsumableArray(neededValues), [hours || null]);
    if (showMinutes) neededValues = [].concat(_toConsumableArray(neededValues), [minutes || null]);
    if (showSeconds) neededValues = [].concat(_toConsumableArray(neededValues), [seconds || null]);
    if (use12Hours) neededValues = [].concat(_toConsumableArray(neededValues), [meridiem || null]);
    return !neededValues.some(function (value) {
      return !value;
    });
  };

  var handleSecondsChange = function handleSecondsChange(e) {
    var value = e.target.value;
    var secondsHandled = parseTimeNumberFromText(value);
    var nextSeconds = getValidTimeNumber(GENERAL_TIME_RANGE, secondsHandled, lastTypedNumber);
    setSeconds(formatSeconds(format, nextSeconds));
    setHasInputBeenTriggered(true);
    focusNextInputIfNeeded(e.target, GENERAL_TIME_RANGE.max, value);
  };

  var handleMinutesChange = function handleMinutesChange(e) {
    var value = e.target.value;
    var minutesHandled = parseTimeNumberFromText(value);
    var nextMinutes = getValidTimeNumber(GENERAL_TIME_RANGE, minutesHandled, lastTypedNumber);
    setMinutes(formatMinutes(format, nextMinutes));
    setHasInputBeenTriggered(true);
    focusNextInputIfNeeded(e.target, GENERAL_TIME_RANGE.max, value);
  };

  var handleHoursChange = function handleHoursChange(e) {
    var value = e.target.value;
    var hour = parseTimeNumberFromText(value);
    var hoursRange = use12Hours ? HOUR_RANGE_12 : HOUR_RANGE_24;
    var nextHour = getValidTimeNumber(hoursRange, hour, lastTypedNumber);
    setHours(formatHour(format, nextHour));
    setHasInputBeenTriggered(true);
    focusNextInputIfNeeded(e.target, hoursRange.max, value);
  };

  var handleMeridiemKeyDown = function handleMeridiemKeyDown(e) {
    if (e.key === ARROW_UP || e.key === 'a') {
      setMeridiem(formatMeridiem(format, AM));
    } else if (e.key === ARROW_DOWN || e.key === 'p') {
      setMeridiem(formatMeridiem(format, PM));
    } else if (e.key === SHIFT) {
      setCurrentKey(e.key);
    } else if (e.key === BACKSPACE) {
      setMeridiem('');
    }

    setHasInputBeenTriggered(true);
  };

  var handleMeridiemBlur = function handleMeridiemBlur(e) {
    var value = e.target.value;

    if (value !== AM && value !== PM) {
      setMeridiem('');
    }
  };

  var handleOnBlur = function handleOnBlur(event) {
    var selectedTime = moment();

    if (clearable && !event.currentTarget.contains(event.relatedTarget)) {
      if (showHours && hours) selectedTime.hours(hours);
      if (showMinutes && minutes) selectedTime.minutes(minutes);
      if (showSeconds && seconds) selectedTime.seconds(seconds);

      if (use12Hours && meridiem) {
        var nextHours = meridiem.toLowerCase() === AM ? hours % 12 : hours % 12 + 12;
        selectedTime.hour(nextHours);
      }

      onChange(selectedTime);
    }
  };

  var renderSecondsInput = function renderSecondsInput() {
    if (!showSeconds) return null;
    return /*#__PURE__*/React.createElement(TimeInput, _extends({}, commonInputProps(function (e) {
      return onKeyDown(e, GENERAL_TIME_RANGE.max);
    }, onInputFocus, onClick), {
      key: "seconds-input",
      "data-testid": "ds-time-input__seconds",
      "aria-label": ariaLabel || ariaLabel2 || 'Seconds Input',
      disabled: disabled // eslint-disable-next-line no-return-assign
      ,
      innerRef: function innerRef(node) {
        return node;
      },
      onChange: handleSecondsChange,
      placeholder: placeholderFormat(PLACEHOLDER_TIME.seconds, format),
      value: seconds
    }));
  };

  var renderMinutesInput = function renderMinutesInput() {
    if (!showMinutes) return null;
    return /*#__PURE__*/React.createElement(TimeInput, _extends({}, commonInputProps(function (e) {
      return onKeyDown(e, GENERAL_TIME_RANGE.max);
    }, onInputFocus, onClick), {
      key: "minutes-input",
      "data-testid": "ds-time-input__minutes",
      "aria-label": ariaLabel || ariaLabel2 || 'Minutes Input',
      disabled: disabled // eslint-disable-next-line no-return-assign
      ,
      innerRef: function innerRef(node) {
        return node;
      },
      onChange: handleMinutesChange,
      placeholder: placeholderFormat(PLACEHOLDER_TIME.minutes, format),
      value: minutes
    }));
  };

  var renderHoursInput = function renderHoursInput() {
    if (!showHours) return null;
    var hourFormatRange = use12Hours ? HOUR_RANGE_12 : HOUR_RANGE_24;
    return /*#__PURE__*/React.createElement(TimeInput, _extends({}, commonInputProps(function (e) {
      return onKeyDown(e, hourFormatRange.max);
    }, onInputFocus, onClick), {
      key: "hour-input",
      "data-testid": "ds-time-input__hours",
      "aria-label": ariaLabel || ariaLabel2 || 'Hours Input',
      disabled: disabled // eslint-disable-next-line no-return-assign
      ,
      innerRef: function innerRef(node) {
        return node;
      },
      onChange: handleHoursChange,
      placeholder: placeholderFormat(PLACEHOLDER_TIME.hour, format, use12Hours),
      value: hours
    }));
  };

  var renderMeridiemInput = function renderMeridiemInput() {
    if (!use12Hours) return null;
    return /*#__PURE__*/React.createElement(TimeInput, _extends({}, commonInputProps, {
      key: "meridiem-input",
      "data-testid": "ds-time-input__ampm",
      "aria-label": ariaLabel || ariaLabel2 || 'Meridiem Input',
      disabled: disabled,
      onBlur: handleMeridiemBlur,
      onKeyDown: handleMeridiemKeyDown,
      placeholder: placeholderFormat(PLACEHOLDER_TIME.meridiem, format),
      value: meridiem
    }));
  };

  var renderTimeInputs = function renderTimeInputs() {
    var inputs = [renderHoursInput(), renderMinutesInput(), renderSecondsInput()];
    /* eslint-disable indent */

    var inputsWithDividers = inputs.reduce(function (acc, input, index) {
      return input ? acc.concat([index ? /*#__PURE__*/React.createElement(TimeDivider // eslint-disable-next-line react/no-array-index-key
      , {
        key: index,
        content: ":",
        value: input.props.value
      }) : null, input]) : acc;
    }, []);
    /* eslint-enable indent */

    return [inputsWithDividers, /*#__PURE__*/React.createElement('span', {
      key: 'span'
    }), // hack for next/prev focus
    renderMeridiemInput()];
  };

  return /*#__PURE__*/React.createElement(InputTimesGroup, {
    onBlur: handleOnBlur,
    innerRef: innerRef
  }, renderTimeInputs());
};

export default TimeInputs;
//# sourceMappingURL=TimeInputs.js.map
